import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../i18n';
import type { DocumentSection } from '../utils/markdownParser';
import StyledMarkdown from './StyledMarkdown';
import MarkdownErrorBoundary from './MarkdownErrorBoundary';
import ParagraphReplyPanel from './ParagraphReplyPanel';
import { useApp } from '../context/useApp';
import type { SectionKey } from '../prompts';
import { Copy, Check, FileOutput, Maximize2, X, Code, ChevronUp, ChevronDown, MessageSquare } from 'lucide-react';

interface SectionCardProps {
  section: DocumentSection;
  projectName: string;
  sectionKey: SectionKey;
  projectId: string;
  sectionContent: string;
  locale?: string;
  onExport: (section: DocumentSection) => void;
}

export default function SectionCard({
  section,
  projectName,
  sectionKey,
  projectId,
  sectionContent,
  locale,
  onExport,
}: SectionCardProps) {
  const { addSectionReply, projects } = useApp();
  const { t } = useLanguage();
  const d = t.document;
  const project = projects.find((p) => p.id === projectId);
  const replies = project?.sections[sectionKey]?.replies || [];

  const [copied, setCopied] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [replyPanel, setReplyPanel] = useState<{ blockId: string; text: string } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const copySection = async () => {
    const text = `# ${section.title}\n\n${section.content}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyHtml = async () => {
    const container = document.createElement('div');
    container.innerHTML = document.getElementById(section.id)?.querySelector('.prose')?.innerHTML || '';
    await navigator.clipboard.writeText(container.innerHTML);
  };

  useEffect(() => {
    if (!focused) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFocused(false);
    };
    document.addEventListener('keydown', handleKey);
    modalRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKey);
  }, [focused]);

  const handleParagraphClick = (blockId: string, text: string) => {
    setReplyPanel({ blockId, text });
  };

  const handleSaveReply = (blockId: string, originalText: string, prompt: string, response: string) => {
    void addSectionReply(projectId, sectionKey, {
      id: crypto.randomUUID(),
      blockId,
      originalText,
      prompt,
      response,
      createdAt: new Date().toISOString(),
    });
  };

  return (
    <>
      <article id={section.id} className="section-card mb-6 scroll-mt-24">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-mist">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5">
                {section.level === 2 ? 'Section' : 'Sub-section'}
              </span>
              <span className="text-xs text-ash flex items-center gap-1">
                <MessageSquare size={12} />
                {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
              </span>
            </div>
            <h2 className="text-xl font-bold text-ink leading-tight">{section.title}</h2>
          </div>
          <div className="flex items-center gap-1 no-print">
            <button
              onClick={() => setCollapsed((v) => !v)}
              className="p-2 text-slate hover:text-red-600 hover:bg-red-50 transition-colors"
              title={collapsed ? d.expand : d.collapse}
              aria-label={collapsed ? d.expand : d.collapse}
            >
              {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
            <button
              onClick={copySection}
              className="p-2 text-slate hover:text-red-600 hover:bg-red-50 transition-colors"
              title={d.copyMarkdown}
            >
              {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
            </button>
            <button
              onClick={copyHtml}
              className="p-2 text-slate hover:text-red-600 hover:bg-red-50 transition-colors"
              title={d.copyHtml}
            >
              <Code size={16} />
            </button>
            <button
              onClick={() => onExport(section)}
              className="p-2 text-slate hover:text-red-600 hover:bg-red-50 transition-colors"
              title={d.export}
            >
              <FileOutput size={16} />
            </button>
            <button
              onClick={() => setFocused(true)}
              className="p-2 text-slate hover:text-red-600 hover:bg-red-50 transition-colors"
              title={d.focus}
            >
              <Maximize2 size={16} />
            </button>
          </div>
        </div>

        {!collapsed && (
          <div className="p-5">
            {showSource ? (
              <pre className="bg-black text-white p-4 overflow-x-auto text-sm">
                <code>{section.content}</code>
              </pre>
            ) : (
              <MarkdownErrorBoundary content={section.content}>
                <StyledMarkdown
                  content={section.content}
                  blockPrefix={section.id}
                  onParagraphClick={handleParagraphClick}
                />
              </MarkdownErrorBoundary>
            )}

            {section.subsections.length > 0 && (
              <div className="mt-6 space-y-4">
                {section.subsections.map((sub) => (
                  <div key={sub.id} id={sub.id} className="border-l-2 border-red-200 pl-4 scroll-mt-24">
                    <h3 className="text-lg font-semibold text-ink mb-2">{sub.title}</h3>
                    <MarkdownErrorBoundary content={sub.content}>
                      <StyledMarkdown
                        content={sub.content}
                        blockPrefix={sub.id}
                        onParagraphClick={handleParagraphClick}
                      />
                    </MarkdownErrorBoundary>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!collapsed && (
          <div className="px-5 pb-3 no-print">
            <button
              onClick={() => setShowSource((v) => !v)}
              className="text-xs text-slate hover:text-red-600 flex items-center gap-1"
            >
              <Code size={12} />
              {showSource ? d.hideSource : d.showSource}
            </button>
          </div>
        )}
      </article>

      {focused && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setFocused(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="focus-title"
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            className="bg-white shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col outline-none border border-cloud"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-cloud">
              <div>
                <p className="text-xs text-slate mb-1">{projectName}</p>
                <h2 id="focus-title" className="text-xl font-bold text-ink">
                  {section.title}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onExport(section)}
                  className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 flex items-center gap-2"
                >
                  <FileOutput size={16} /> {d.export}
                </button>
                <button
                  onClick={() => setFocused(false)}
                  className="p-2 text-slate hover:bg-mist"
                  aria-label="Close focus view"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto">
              <MarkdownErrorBoundary content={`# ${section.title}\n\n${section.content}`}>
                <StyledMarkdown content={`# ${section.title}\n\n${section.content}`} />
              </MarkdownErrorBoundary>
              {section.subsections.map((sub) => (
                <div key={sub.id} className="mt-8">
                  <MarkdownErrorBoundary content={`## ${sub.title}\n\n${sub.content}`}>
                    <StyledMarkdown content={`## ${sub.title}\n\n${sub.content}`} />
                  </MarkdownErrorBoundary>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {replyPanel && (
        <ParagraphReplyPanel
          blockId={replyPanel.blockId}
          originalText={replyPanel.text}
          sectionTitle={section.title}
          sectionContent={sectionContent}
          projectName={projectName}
          locale={locale}
          existingReplies={replies
            .filter((r) => r.blockId === replyPanel.blockId)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())}
          onSave={handleSaveReply}
          onClose={() => setReplyPanel(null)}
        />
      )}
    </>
  );
}
